export type Category = 'focal' | 'oled' | 'gaming' | 'graphic' | 'curved' | 'popular' | 'portable';
export type Resolution = 'FHD' | '2K' | '4K' | 'UWQHD' | 'WQHD' | '8K';
export type Status = 'active' | 'discontinued' | 'incoming';

export interface Monitor {
  id: string;
  name: string;
  brand: string;
  category: Category;
  size: number | null;
  resolution: Resolution | null;
  refresh_rate: number | null;
  panel_type: string | null;
  tags: string | null;
  is_focal: number;
  is_exclusive: number;
  is_oled_focus: number;
  is_branding: number;
  price_msrp: number | null;
  price_ct: number | null;
  price_web: number | null;
  discount_pct: number | null;
  margin_vnd: number | null;
  margin_pct: number | null;
  npp: string | null;
  node_raw: string | null;
  thumb_url: string | null;
  status: Status;
  created_at: string;
  updated_at: string;
}

export interface BrandStat {
  brand: string;
  count: number;
  focal_count: number;
}

export interface Stats {
  total_skus: number;
  active_skus: number;
  focal_count: number;
  oled_count: number;
  gaming_count: number;
  brand_count: number;
  last_import: string | null;
}

export interface Meta {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

interface ApiOk<T> {
  ok: true;
  data: T;
  meta?: Meta;
}
interface ApiErr {
  ok: false;
  error: string;
}
type ApiResponse<T> = ApiOk<T> | ApiErr;

class ApiError extends Error {
  constructor(message: string, public readonly status?: number) {
    super(message);
    this.name = 'ApiError';
  }
}

async function fetchJSON<T>(url: string): Promise<{ data: T; meta?: Meta }> {
  const res = await fetch(url);
  let json: ApiResponse<T>;
  try {
    json = (await res.json()) as ApiResponse<T>;
  } catch {
    throw new ApiError(`Invalid JSON response (HTTP ${res.status})`, res.status);
  }
  if (!json.ok) throw new ApiError(json.error, res.status);
  return { data: json.data, meta: json.meta };
}

export interface MonitorListParams {
  brand?: string;
  category?: Category;
  resolution?: Resolution;
  size?: number;
  refresh_min?: number;
  is_focal?: '0' | '1';
  status?: Status;
  search?: string;
  sort?: string;
  page?: number;
  limit?: number;
}

export interface MonitorListResult {
  data: Monitor[];
  meta: Meta;
}

export async function listMonitors(params: MonitorListParams = {}): Promise<MonitorListResult> {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '' && value !== null) qs.set(key, String(value));
  }
  const url = `/api/monitors${qs.toString() ? `?${qs}` : ''}`;
  const { data, meta } = await fetchJSON<Monitor[]>(url);
  return { data, meta: meta ?? { total: data.length, page: 1, limit: data.length, pages: 1 } };
}

export async function getMonitor(id: string): Promise<Monitor> {
  const { data } = await fetchJSON<Monitor>(`/api/monitors/${encodeURIComponent(id)}`);
  return data;
}

export async function getBrands(): Promise<BrandStat[]> {
  const { data } = await fetchJSON<BrandStat[]>('/api/brands');
  return data;
}

export async function getStats(): Promise<Stats> {
  const { data } = await fetchJSON<Stats>('/api/stats');
  return data;
}

// ---- Brand Report ----
export interface ReportTrendPoint {
  month: string;
  units_sold: number;
  revenue_vnd: number;
  margin_vnd: number;
  margin_pct: number;
}

export interface ReportTotals {
  units_sold: number;
  revenue_vnd: number;
  margin_vnd: number;
  margin_pct: number;
  delta_revenue_pct: number;
  delta_units_pct: number;
  market_share_revenue_pct: number;
  market_share_units_pct: number;
}

export interface ReportSegment {
  category: string;
  sku_count: number;
  focal_count: number;
  units_sold: number;
  revenue_vnd: number;
  share_pct: number;
}

export interface ReportMonitor extends Monitor {
  sales_units: number;
  sales_revenue: number;
  sales_margin: number;
}

export interface ReportPeriod {
  value: string;       // 'all' | 'YYYY-QN' | 'YYYY-MM'
  label: string;       // 'Tất cả' | 'Q1 2026' | 'T3/2026'
  from: string | null;
  to: string | null;
}

export interface ReportCompareBlock {
  period: ReportPeriod;
  months: string[];
  trend: ReportTrendPoint[];
  totals: ReportTotals;
  segments: ReportSegment[];
}

export interface BrandReport {
  brand: string;
  total_skus: number;
  focal_count: number;
  months: string[];
  trend: ReportTrendPoint[];
  totals: ReportTotals;
  segments: ReportSegment[];
  monitors: ReportMonitor[];
  period: ReportPeriod;
  compare: ReportCompareBlock | null;
}

export interface BrandReportOptions {
  period?: string;   // 'all' | 'YYYY-QN' | 'YYYY-MM'
  compare?: string;
}

export async function getBrandReport(brand: string, options: BrandReportOptions = {}): Promise<BrandReport> {
  const qs = new URLSearchParams();
  if (options.period && options.period !== 'all') qs.set('period', options.period);
  if (options.compare) qs.set('compare', options.compare);
  const q = qs.toString();
  const url = `/api/report/${encodeURIComponent(brand)}${q ? `?${q}` : ''}`;
  const { data } = await fetchJSON<BrandReport>(url);
  return data;
}

export interface AvailablePeriods {
  months: string[];
  quarters: string[];
}

export async function getAvailablePeriods(): Promise<AvailablePeriods> {
  const { data } = await fetchJSON<AvailablePeriods>('/api/sales/periods');
  return data;
}

// ---- Share tokens ----
export interface ShareToken {
  token: string;
  brand: string;
  report_id: string | null;
  description: string | null;
  created_at: string;
  expires_at: string | null;
  revoked: number;
  last_viewed_at: string | null;
}

export interface CreateShareInput {
  /** Either report_id (preferred — frozen snapshot) OR brand (legacy live mode) */
  report_id?: string;
  brand?: string;
  description?: string;
  expires_days?: number;
}

export interface CreatedShare {
  token: string;
  brand: string;
  report_id: string | null;
  description: string | null;
  expires_at: string;
}

export interface ReportSummary {
  period: ReportPeriod;
  months: string[];
  trend: ReportTrendPoint[];
  totals: ReportTotals;
  segments: ReportSegment[];
}

export interface ComparePair {
  current: ReportSummary;
  previous: ReportSummary;
}

export interface AutoCompare {
  qoq: ComparePair | null;
  mom: ComparePair | null;
}

export interface SharedReport extends Omit<BrandReport, 'compare'> {
  compare: null;
  auto_compare: AutoCompare | null;
  /** Present only when token resolves to a specific report (Phase 5+) */
  report?: {
    id: string;
    title: string;
    description: string | null;
    period_start: string;
    period_end: string;
  };
  share: {
    description: string | null;
    expires_at: string | null;
  };
}

export async function createShare(input: CreateShareInput): Promise<CreatedShare> {
  const res = await fetch('/api/admin/share', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const json = (await res.json()) as ApiOk<CreatedShare> | ApiErr;
  if (!json.ok) throw new ApiError(json.error, res.status);
  return json.data;
}

export async function listShares(brand: string): Promise<ShareToken[]> {
  const { data } = await fetchJSON<ShareToken[]>(`/api/admin/share?brand=${encodeURIComponent(brand)}`);
  return data;
}

export async function listSharesByReport(reportId: string): Promise<ShareToken[]> {
  const { data } = await fetchJSON<ShareToken[]>(`/api/admin/share?report_id=${encodeURIComponent(reportId)}`);
  return data;
}

export async function revokeShare(token: string): Promise<void> {
  const res = await fetch(`/api/admin/share/${encodeURIComponent(token)}`, { method: 'DELETE' });
  const json = (await res.json()) as ApiOk<unknown> | ApiErr;
  if (!json.ok) throw new ApiError(json.error, res.status);
}

export async function getSharedReport(token: string): Promise<SharedReport> {
  const { data } = await fetchJSON<SharedReport>(`/api/share/${encodeURIComponent(token)}`);
  return data;
}

// ---- Import ----
export interface ImportRow {
  id: string;
  name: string;
  brand: string;
  category: Category;
  size?: number | null;
  resolution?: Resolution | null;
  refresh_rate?: number | null;
  panel_type?: string | null;
  tags?: string | null;
  is_focal?: 0 | 1;
  is_exclusive?: 0 | 1;
  is_oled_focus?: 0 | 1;
  is_branding?: 0 | 1;
  price_msrp?: number | null;
  price_ct?: number | null;
  price_web?: number | null;
  discount_pct?: number | null;
  margin_vnd?: number | null;
  margin_pct?: number | null;
  npp?: string | null;
  node_raw?: string | null;
  thumb_url?: string | null;
  status?: Status;
}

export type ImportRowStatus = 'new' | 'update' | 'error';

export interface ImportRowResult {
  index: number;
  id: string | null;
  status: ImportRowStatus;
  error?: string;
}

export interface ImportSummary {
  total: number;
  new: number;
  update: number;
  errors: number;
}

export interface ImportResponse {
  dry_run: boolean;
  summary: ImportSummary;
  rows: ImportRowResult[];
  inserted?: number;
  updated?: number;
}

export async function importMonitors(rows: ImportRow[], dryRun: boolean): Promise<ImportResponse> {
  const url = `/api/admin/import${dryRun ? '?dry_run=1' : ''}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ rows }),
  });
  const json = (await res.json()) as ApiOk<ImportResponse> | ApiErr;
  if (!json.ok) throw new ApiError(json.error, res.status);
  return json.data;
}

// ---- Sales monthly import ----
export interface SalesImportRow {
  monitor_id: string;
  month: string; // YYYY-MM
  units_sold: number;
  revenue_vnd: number;
  margin_vnd: number;
  stock_qty: number;
}

export interface SalesImportRowResult {
  index: number;
  monitor_id: string | null;
  month: string | null;
  status: ImportRowStatus;
  error?: string;
}

export interface SalesImportResponse {
  dry_run: boolean;
  summary: ImportSummary;
  rows: SalesImportRowResult[];
  inserted?: number;
  updated?: number;
}

export async function importSales(rows: SalesImportRow[], dryRun: boolean): Promise<SalesImportResponse> {
  const url = `/api/admin/import/sales${dryRun ? '?dry_run=1' : ''}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ rows }),
  });
  const json = (await res.json()) as ApiOk<SalesImportResponse> | ApiErr;
  if (!json.ok) throw new ApiError(json.error, res.status);
  return json.data;
}

// ---- Reports CRUD ----
export type ReportStatus = 'draft' | 'published' | 'archived';

export interface ReportListItem {
  id: string;
  brand: string;
  period_start: string;  // YYYY-MM
  period_end: string;
  title: string;
  description: string | null;
  status: ReportStatus;
  created_at: string;
  updated_at: string;
}

export interface Report extends ReportListItem {
  period_label: string;
  snapshot: BrandReport | null;
}

export interface ReportListParams {
  brand?: string;
  status?: ReportStatus;
  period?: string;  // 'YYYY-MM' | 'YYYY-QN'
  page?: number;
  limit?: number;
}

export interface ReportListResult {
  data: ReportListItem[];
  meta: Meta;
}

export interface ReportCreateInput {
  brand: string;
  period_start: string;
  period_end: string;
  title: string;
  description?: string;
  status?: ReportStatus;
  sales_rows?: SalesImportRow[];
}

export interface ReportPatchInput {
  title?: string;
  description?: string;
  status?: ReportStatus;
  sales_rows?: SalesImportRow[];
}

export interface ReportCreateResult {
  id: string;
  brand: string;
  period_start: string;
  period_end: string;
  title: string;
  description: string | null;
  status: ReportStatus;
  sales_upserted: number;
  import_warnings: string[];
}

export async function listReports(params: ReportListParams = {}): Promise<ReportListResult> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '' && v !== null) qs.set(k, String(v));
  }
  const url = `/api/admin/reports${qs.toString() ? `?${qs}` : ''}`;
  const { data, meta } = await fetchJSON<ReportListItem[]>(url);
  return { data, meta: meta ?? { total: data.length, page: 1, limit: data.length, pages: 1 } };
}

export async function getReport(id: string): Promise<Report> {
  const { data } = await fetchJSON<Report>(`/api/admin/reports/${encodeURIComponent(id)}`);
  return data;
}

export async function createReport(input: ReportCreateInput): Promise<ReportCreateResult> {
  const res = await fetch('/api/admin/reports', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const json = (await res.json()) as ApiOk<ReportCreateResult> | ApiErr;
  if (!json.ok) throw new ApiError(json.error, res.status);
  return json.data;
}

export async function patchReport(
  id: string,
  patch: ReportPatchInput
): Promise<{ id: string; sales_upserted: number; snapshot_updated: boolean; import_warnings: string[] }> {
  const res = await fetch(`/api/admin/reports/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  const json = (await res.json()) as ApiOk<{ id: string; sales_upserted: number; snapshot_updated: boolean; import_warnings: string[] }> | ApiErr;
  if (!json.ok) throw new ApiError(json.error, res.status);
  return json.data;
}

export async function deleteReport(id: string): Promise<void> {
  const res = await fetch(`/api/admin/reports/${encodeURIComponent(id)}`, { method: 'DELETE' });
  const json = (await res.json()) as ApiOk<unknown> | ApiErr;
  if (!json.ok) throw new ApiError(json.error, res.status);
}

export async function regenerateReport(id: string): Promise<void> {
  const res = await fetch(`/api/admin/reports/${encodeURIComponent(id)}/regenerate`, { method: 'POST' });
  const json = (await res.json()) as ApiOk<unknown> | ApiErr;
  if (!json.ok) throw new ApiError(json.error, res.status);
}

export { ApiError };
